import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { TaxRateData } from '@/lib/taxCalculator';

/**
 * Read-only access to the shop's tax configuration:
 * - active rows from `tax_rates`
 * - the `oss_mode` toggle from `shop_settings` (country specific VAT on/off)
 *
 * Purely for display purposes — no price is ever recalculated from this.
 */
export function useTaxRates() {
  const ratesQuery = useQuery({
    queryKey: ['tax-rates-public'],
    staleTime: 1000 * 60 * 30,
    queryFn: async (): Promise<TaxRateData[]> => {
      const { data, error } = await supabase
        .from('tax_rates')
        .select('country_code, country_name, tax_rate, reduced_rate, tax_name, is_eu, tax_type, notes')
        .eq('is_active', true);
      if (error) throw error;
      return (data || []).map((r: any) => ({
        country_code: r.country_code,
        country_name: r.country_name,
        tax_rate: Number(r.tax_rate),
        reduced_rate: r.reduced_rate != null ? Number(r.reduced_rate) : undefined,
        tax_name: r.tax_name || 'MwSt',
        is_eu: !!r.is_eu,
        tax_type: r.tax_type || undefined,
        notes: r.notes || undefined,
      }));
    },
  });

  const ossQuery = useQuery({
    queryKey: ['shop-setting-oss-mode'],
    staleTime: 1000 * 60 * 30,
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await supabase
        .from('shop_settings')
        .select('value')
        .eq('key', 'oss_mode')
        .maybeSingle();
      if (error) throw error;
      const val = data?.value as { enabled?: boolean } | null;
      return !!val?.enabled;
    },
  });

  return {
    taxRates: ratesQuery.data ?? [],
    useLocalRates: ossQuery.data ?? false,
    isLoading: ratesQuery.isLoading || ossQuery.isLoading,
  };
}
